import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Slider } from '@heroui/react';
import {
  ICON_TARGET_SIDE,
  encodeIconCanvas,
  ImageUploadError,
} from '@/lib/imageUpload';
import { clampOffsets } from '@/lib/cropMath';

const VIEW = 280; // square crop viewport, px
const MAX_ZOOM = 3;
// How far below "fit the whole image" the user may shrink — leaves padding
// around a logo. 0.5 = the longest side can shrink to half the square.
const MIN_FIT_FACTOR = 0.5;

/**
 * Square pan/zoom cropper for icon uploads. Lets the user position + scale the
 * picked image inside a circular/square frame before it's center-cropped, so a
 * tall logo no longer lands off-centre. Static images only — GIFs keep the
 * auto-crop path (animated re-crop is out of scope). Built on HeroUI Modal /
 * Slider / Button; the geometry lives in the tested `cropMath`.
 */
export function ImageCropModal(props: {
  file: File | null;
  /** Round preview matches a circular avatar; square otherwise. */
  round?: boolean;
  onCancel: () => void;
  onCropped: (dataUrl: string) => void;
}) {
  const { t } = useTranslation();
  const { file } = props;
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const drag = useRef<{ px: number; py: number; ox: number; oy: number } | null>(null);

  // Load the picked file into an <img> for display + canvas source.
  useEffect(() => {
    setImg(null);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setError(null);
    if (!file) return;
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      setImg(image);
      // Default to FILLING the square (cover) so an icon reads as a proper
      // icon — a wide logo otherwise lands tiny with padding and looks lost in
      // the round server rail. The slider still goes below this to add padding.
      setZoom(1);
    };
    image.onerror = () => setError(t('imageCrop.readError'));
    image.src = url;
    return () => URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on `file`; `t` is referentially stable and only read in the error path
  }, [file]);

  if (!file) return null;

  const open = file !== null;
  const hasAlpha = file.type === 'image/png' || file.type === 'image/webp';

  // `zoom` is relative to the cover scale (zoom 1 = shortest side fills the
  // square). The whole image fits at zoom = min/max side; allow shrinking below
  // that (down to MIN_FIT_FACTOR) so a logo can sit with padding.
  const fitZoom = img ? Math.min(img.width, img.height) / Math.max(img.width, img.height) : 1;
  const minZoom = fitZoom * MIN_FIT_FACTOR;

  // Rendered image box for the current zoom (cover scale × zoom).
  const scale = img ? (VIEW / Math.min(img.width, img.height)) * zoom : 1;
  const renderedW = img ? img.width * scale : VIEW;
  const renderedH = img ? img.height * scale : VIEW;
  const left = (VIEW - renderedW) / 2 + offset.x;
  const top = (VIEW - renderedH) / 2 + offset.y;

  function onPointerDown(e: React.PointerEvent) {
    if (!img) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { px: e.clientX, py: e.clientY, ox: offset.x, oy: offset.y };
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!drag.current || !img) return;
    const nx = drag.current.ox + (e.clientX - drag.current.px);
    const ny = drag.current.oy + (e.clientY - drag.current.py);
    setOffset(clampOffsets(VIEW, img.width, img.height, zoom, nx, ny));
  }
  function endDrag() {
    drag.current = null;
  }
  function onZoom(next: number) {
    const z = Math.max(minZoom, Math.min(MAX_ZOOM, next));
    setZoom(z);
    if (img) setOffset((o) => clampOffsets(VIEW, img.width, img.height, z, o.x, o.y));
  }

  async function confirm() {
    if (!img) return;
    setBusy(true);
    setError(null);
    try {
      // Render the icon canvas EXACTLY as the viewport shows it: same image
      // box, scaled up to the target side. Cropping (zoom-in) is handled by
      // canvas clipping; padding (zoom-out) leaves a filled background.
      const k = ICON_TARGET_SIDE / VIEW;
      const canvas = document.createElement('canvas');
      canvas.width = ICON_TARGET_SIDE;
      canvas.height = ICON_TARGET_SIDE;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new ImageUploadError('invalid', t('imageCrop.noCanvas'));
      // Opaque formats (JPEG) need a background or the gutters render black;
      // alpha formats (PNG/WEBP) keep the padding transparent.
      if (!hasAlpha) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, ICON_TARGET_SIDE, ICON_TARGET_SIDE);
      }
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, left * k, top * k, renderedW * k, renderedH * k);
      const dataUrl = await encodeIconCanvas(canvas, hasAlpha);
      props.onCropped(dataUrl);
    } catch (err) {
      setError(
        err instanceof ImageUploadError ? err.message : t('imageCrop.processError'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal.Backdrop isOpen={open} onOpenChange={(o) => { if (!o) props.onCancel(); }}>
      <Modal.Container>
        <Modal.Dialog className="w-[340px] max-w-[92vw]">
            <Modal.Header>
              <Modal.Heading>{t('imageCrop.heading')}</Modal.Heading>
            </Modal.Header>
            <Modal.Body className="flex flex-col items-center gap-4">
              <div
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                className={
                  'relative cursor-grab touch-none overflow-hidden bg-black/40 active:cursor-grabbing ' +
                  (props.round ? 'rounded-full' : 'rounded-2xl')
                }
                style={{ width: VIEW, height: VIEW }}
              >
                {img && (
                  <img
                    src={img.src}
                    alt=""
                    draggable={false}
                    className="pointer-events-none absolute max-w-none select-none"
                    style={{ width: renderedW, height: renderedH, left, top }}
                  />
                )}
                {/* Frame outline so the user sees the crop edge. */}
                <div
                  className={
                    'pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/30 ' +
                    (props.round ? 'rounded-full' : 'rounded-2xl')
                  }
                />
              </div>

              <div className="flex w-full items-center gap-3">
                <span className="shrink-0 select-none text-sm text-ink-muted">−</span>
                <Slider
                  aria-label={t('imageCrop.zoom')}
                  className="flex-1"
                  minValue={minZoom}
                  maxValue={MAX_ZOOM}
                  step={0.01}
                  value={Math.max(minZoom, Math.min(MAX_ZOOM, zoom))}
                  onChange={(v) => onZoom(Array.isArray(v) ? (v[0] ?? 1) : v)}
                >
                  <Slider.Track>
                    <Slider.Fill />
                    <Slider.Thumb />
                  </Slider.Track>
                </Slider>
                <span className="shrink-0 select-none text-sm text-ink-muted">+</span>
              </div>

              {error && <p className="text-xs text-danger">{error}</p>}
            </Modal.Body>
            <Modal.Footer className="flex justify-end gap-2">
              <Button variant="secondary" onPress={props.onCancel} isDisabled={busy}>
                {t('cmod.cancel')}
              </Button>
              <Button onPress={() => void confirm()} isDisabled={!img || busy}>
                {busy ? t('imageCrop.processing') : t('imageCrop.apply')}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
    </Modal.Backdrop>
  );
}
